import { Typography } from '@common/components/foundations/Typography';
import { Card } from '@common/components/molecules/Card';
import { useTailwindColor } from '@common/hooks/useTailwindColors';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';

import dayjs from 'dayjs';
import { formattedCurrency } from '@pages/Client/PricingStrategy/MarketIntelligence/utils/formatedCurrency';
import { useCallback, useRef, useState } from 'react';
import clsx from 'clsx';
import { API_DATE_FORMAT } from '@common/constants/date';
import { usePriceDrawerStore } from '@common/store/priceDrawer';

type DataKey = 'pms_price' | 'pickup_boost_price';

export interface ChartValue {
  date: string;
  pms_price: number | null | undefined;
  pickup_boost_price: number | null | undefined;
}

interface DataProps {
  data: ChartValue[];
}

export const PickupBoostPriceChart = ({ data }: DataProps) => {
  const grey = useTailwindColor('grey');
  const gold = useTailwindColor('gold');
  const green = useTailwindColor('uiGreen');
  const mediumGrey = useTailwindColor('mediumGrey');
  const fontFamily = "'Inter var', sans-serif";
  const fontSize = '10px';
  const fontWeight = 400;
  const [seriesVisibility, setSeriesVisibility] = useState<Record<DataKey, boolean>>({
    pickup_boost_price: true,
    pms_price: true
  });

  const { setDrawerState, setViewingDate } = usePriceDrawerStore();

  const lastLabelDisplayedRef = useRef('');

  const CustomTooltip: React.FC<any> = (props) => {
    const { active, payload } = props;
    if (!active || !payload || !payload[0]) return null;
    const dataPoint = payload[0].payload;
    return (
      <Card backgroundColor="white" className="bg-opacity-90 backdrop-blur">
        <Typography variant="meta-1" className="font-semibold" color="darkGrey">
          {dayjs(dataPoint.date).format('ddd, DD MMM YYYY')}
        </Typography>
        <div className="flex items-center gap-2">
          <div className="h-2 w-2 rounded-full bg-uiGreen" />
          <Typography variant="meta-2" color="darkGrey">
            Pickup Boost Price: <b>{formattedCurrency(dataPoint.pickup_boost_price)}</b>
          </Typography>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-2 w-2 rounded-full bg-gold" />
          <Typography variant="meta-2" color="darkGrey">
            Price in PMS: <b>{formattedCurrency(dataPoint.pms_price)}</b>
          </Typography>
        </div>
        <div className="mt-2 text-xs italic text-copyTextGrey">
          Click the chart for more details
        </div>
      </Card>
    );
  };

  const customLegendPayload = [
    {
      dataKey: 'pickup_boost_price',
      value: 'Pickup Boost Price',
      color: green
    },

    {
      dataKey: 'pms_price',
      value: 'Price in PMS',
      color: gold
    }
  ];

  const dataMaxPickupBoost = Math.floor(
    Math.max(...data.map((item) => item.pickup_boost_price as number))
  );
  const dataMaxPMSPrice = Math.floor(
    Math.max(...data.map((item) => item.pickup_boost_price as number))
  );
  const dataMax = Math.floor(Math.max(dataMaxPickupBoost, dataMaxPMSPrice) * 1.1); // 10% padding

  const dataMinPickupBoost = Math.floor(
    Math.min(...data.map((item) => item.pickup_boost_price as number))
  );
  const dataMinPMSPrice = Math.floor(
    Math.min(...data.map((item) => item.pickup_boost_price as number))
  );

  const dataMin = Math.floor(Math.min(dataMinPickupBoost, dataMinPMSPrice) * 0.9); // -10% padding

  const CustomLegend: React.FC<any> = (props) => {
    const { payload } = props;
    // Add onClick handler to toggle series visibility
    const handleLegendClick = (dataKey: DataKey) => {
      setSeriesVisibility((prev) => ({ ...prev, [dataKey]: !prev[dataKey] }));
    };

    return (
      <ul className="mb-8 flex list-none justify-center">
        {payload?.map(
          (entry: { dataKey: DataKey; value: string; color: string }, index: number) => (
            <li
              key={`item-${index}`}
              className="mr-2 flex cursor-pointer items-center"
              onClick={() => handleLegendClick(entry.dataKey)}>
              <div
                className="mr-1 h-3 w-3 rounded-full"
                style={{
                  backgroundColor: entry.color,
                  opacity: seriesVisibility[entry.dataKey] ? 1 : 0.3
                }}
              />
              <Typography
                color="copyTextGrey"
                variant="meta-2"
                className={clsx(seriesVisibility[entry.dataKey] ? 'opacity-100' : 'opacity-30')}>
                {entry.value}
              </Typography>
            </li>
          )
        )}
      </ul>
    );
  };
  const xAxisTickFormatter = useCallback((value: any) => {
    const currentLabel = dayjs(value).tz().format('MMM YYYY');

    if (currentLabel === lastLabelDisplayedRef.current) {
      return '';
    }

    lastLabelDisplayedRef.current = currentLabel;
    return currentLabel;
  }, []);

  const handleOpenPriceDrawer = (date: string | undefined) => {
    if (!date) return;
    const formattedDate = new Date(dayjs(date).format(API_DATE_FORMAT));
    setDrawerState(true);
    setViewingDate(formattedDate);
  };

  return (
    <Card backgroundColor="white" className="p-0 shadow-none ">
      <ResponsiveContainer width="100%" height={500}>
        <LineChart
          data={data}
          onClick={(data: any) => {
            handleOpenPriceDrawer(data?.activeLabel);
          }}>
          <CartesianGrid stroke={mediumGrey} strokeDasharray="2" vertical={false} />
          <Tooltip content={(props) => <CustomTooltip {...props} />} />
          <XAxis
            axisLine={false}
            tick={{ fill: grey }}
            tickCount={8}
            tickLine={false}
            fontFamily={fontFamily}
            fontSize={fontSize}
            fontWeight={fontWeight}
            tickFormatter={xAxisTickFormatter}
            dataKey={'date'}
          />
          <YAxis
            axisLine={false}
            domain={[dataMin, dataMax]}
            tick={{ fill: grey }}
            tickCount={8}
            tickLine={false}
            fontFamily={fontFamily}
            fontSize={fontSize}
            fontWeight={fontWeight}
            allowDataOverflow={true}
            tickFormatter={(value) => formattedCurrency(value)}
          />
          <Legend
            verticalAlign="top"
            content={(props) => <CustomLegend {...props} payload={customLegendPayload} />}
          />

          <Line
            type="monotone"
            dataKey="pms_price"
            strokeWidth={2}
            stroke={gold}
            dot={false}
            visibility={seriesVisibility.pms_price ? 'visible' : 'hidden'}
          />
          <Line
            type="monotone"
            dataKey="pickup_boost_price"
            stroke={green}
            strokeWidth={2}
            dot={false}
            visibility={seriesVisibility.pickup_boost_price ? 'visible' : 'hidden'}
          />
        </LineChart>
      </ResponsiveContainer>
    </Card>
  );
};
